'use client';

import React, { type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  PopoverWrapper,
  PopoverStickyHeader,
  PopoverFooterShadow,
  PopoverBody,
} from '@/app/common/components/Popover/styled.tsx';
import OverlayContext from '@/app/common/context/OverlayContext.ts';
import useScrollShadows from '@/app/common/hooks/useScrollShadows.ts';

// keep in sync with pill CSS transition duration
const TRANSITION_TRACK_MS = 400;

export type PopoverPlacement = 'top' | 'bottom';
export type PopoverAlign = 'left' | 'center' | 'right' | 'start';
export type PopoverTrigger = 'click' | 'hover' | 'both';

export type PopoverProps = {
  id: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  placement?: PopoverPlacement;
  align?: PopoverAlign;
  children: ReactNode;
  'aria-label'?: string;
  padding?: string;
  borderRadius?: string;
  width?: string;
  maxWidth?: string;
  maxHeight?: string;
  boxShadow?: string;
  trigger?: PopoverTrigger;
  className?: string;
  closeOnScroll?: boolean;
  title?: ReactNode;
  footer?: ReactNode;
  usePortal?: boolean;
};

const VERTICAL_GAP = 4;
const HORIZONTAL_MARGIN = 8;

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getVisibleFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true' && !el.inert && el.offsetParent !== null
  );

// The panel is portalled to the end of <body>, so Tab past its last control would leave the document.
// The trigger's own neighbour is where the reading order says the panel ends.
const neighbourOfTrigger = (trigger: HTMLElement | null, container: HTMLElement): HTMLElement | undefined => {
  if (!trigger) return undefined;
  const outside = getVisibleFocusableElements(document.body).filter((el) => !container.contains(el));
  const at = outside.indexOf(trigger);
  return at === -1 ? undefined : outside[at + 1];
};

// Safari and Firefox don't focus a clicked <button>, so fall back to the trigger's own control.
const findRestoreTarget = (anchor: HTMLElement): HTMLElement => {
  const active = document.activeElement;
  if (active instanceof HTMLElement && anchor.contains(active)) return active;
  if (anchor.matches(FOCUSABLE_SELECTOR)) return anchor;
  return anchor.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? anchor;
};

const pickVerticalPlacement = (
  preferred: PopoverPlacement,
  anchorRect: DOMRect,
  popoverHeight: number
): PopoverPlacement => {
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const spaceAbove = anchorRect.top;
  if (preferred === 'bottom')
    return spaceBelow >= popoverHeight + VERTICAL_GAP || spaceBelow >= spaceAbove ? 'bottom' : 'top';
  return spaceAbove >= popoverHeight + VERTICAL_GAP || spaceAbove >= spaceBelow ? 'top' : 'bottom';
};

const resolveLeft = (align: PopoverAlign, anchorRect: DOMRect, popoverWidth: number): number => {
  let left: number;
  if (align === 'right') {
    left = anchorRect.right - popoverWidth;
  } else if (align === 'left') {
    left = anchorRect.left;
  } else if (align === 'start') {
    const leftAligned = anchorRect.left;
    left =
      leftAligned + popoverWidth + HORIZONTAL_MARGIN > window.innerWidth
        ? anchorRect.right - popoverWidth
        : leftAligned;
  } else {
    left = anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
  }
  return Math.max(HORIZONTAL_MARGIN, Math.min(left, window.innerWidth - popoverWidth - HORIZONTAL_MARGIN));
};

const resolveTop = (placement: PopoverPlacement, anchorRect: DOMRect, popoverHeight: number): number => {
  const effectivePlacement = pickVerticalPlacement(placement, anchorRect, popoverHeight);
  return effectivePlacement === 'bottom'
    ? anchorRect.bottom + VERTICAL_GAP
    : anchorRect.top - popoverHeight - VERTICAL_GAP;
};

const computePortalCoords = (
  anchorEl: HTMLElement,
  popoverEl: HTMLElement,
  placement: PopoverPlacement,
  align: PopoverAlign
): { top: number; left: number } => {
  const anchorRect = anchorEl.getBoundingClientRect();
  return {
    top: resolveTop(placement, anchorRect, popoverEl.offsetHeight),
    left: resolveLeft(align, anchorRect, popoverEl.offsetWidth),
  };
};

const computeInlineCoords = (
  anchorEl: HTMLElement,
  popoverEl: HTMLElement,
  placement: PopoverPlacement,
  align: PopoverAlign
): { top: number; left: number } => {
  const anchorRect = anchorEl.getBoundingClientRect();
  const parentRect = (popoverEl.offsetParent as HTMLElement | null)?.getBoundingClientRect() ?? { top: 0, left: 0 };
  return {
    top: resolveTop(placement, anchorRect, popoverEl.offsetHeight) - parentRect.top,
    left: resolveInlineLeft(align, anchorRect, popoverEl.offsetWidth, parentRect.left),
  };
};

const resolveInlineLeft = (
  align: PopoverAlign,
  anchorRect: DOMRect,
  popoverWidth: number,
  parentLeft: number
): number => {
  if (align === 'right') return anchorRect.right - popoverWidth - parentLeft;
  if (align === 'left') return anchorRect.left - parentLeft;
  return anchorRect.left + anchorRect.width / 2 - popoverWidth / 2 - parentLeft;
};

export const bindScrollAndResize = (onScroll: (e: Event) => void, onResize: () => void): (() => void) => {
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onResize);
  return () => {
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onResize);
  };
};

const bindKeyboard = (onClose: () => void): (() => void) => {
  const handler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };
  document.addEventListener('keydown', handler);
  return () => document.removeEventListener('keydown', handler);
};

const bindOutsideClick = (anchor: HTMLElement, popover: HTMLElement | null, onClose: () => void): (() => void) => {
  const handler = (e: MouseEvent) => {
    if (popover?.contains(e.target as Node) || anchor.contains(e.target as Node)) return;
    onClose();
  };
  document.addEventListener('mousedown', handler);
  return () => document.removeEventListener('mousedown', handler);
};

const bindListeners = (
  anchor: HTMLElement,
  popover: HTMLElement | null,
  trigger: PopoverTrigger,
  onClose: () => void,
  computePosition: () => void,
  closeOnScroll: boolean
): (() => void) => {
  let rafId: number | null = null;

  const trackPositionThroughTransition = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    const start = performance.now();
    const tick = () => {
      computePosition();
      if (performance.now() - start < TRANSITION_TRACK_MS) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };
    rafId = requestAnimationFrame(tick);
  };

  const onScroll = (e: Event) => {
    if (popover?.contains(e.target as Node)) return;
    if (closeOnScroll) onClose();
    else trackPositionThroughTransition();
  };

  const cleanups = [
    bindScrollAndResize(onScroll, computePosition),
    bindKeyboard(onClose),
    ...(trigger !== 'hover' ? [bindOutsideClick(anchor, popover, onClose)] : []),
  ];

  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    cleanups.forEach((fn) => fn());
  };
};

const elevateParentCard = (anchorEl: HTMLElement): (() => void) | undefined => {
  const card = anchorEl.closest<HTMLElement>('[data-testid="card-container"]');
  if (!card) return undefined;
  card.style.zIndex = '2';
  return () => {
    card.style.zIndex = '';
  };
};

const manageFocus = (
  container: HTMLElement,
  anchor: HTMLElement,
  restoreTo: HTMLElement | null,
  onClose: () => void
): (() => void) => {
  let restoreTarget = restoreTo;
  const focusable = getVisibleFocusableElements(container);
  const rafId = requestAnimationFrame(() => (focusable[0] ?? container).focus());

  // Only focus landing elsewhere closes it; focus that drops to <body> (an option disabled mid-refresh) or
  // leaves the window doesn't.
  const onFocusIn = (e: FocusEvent) => {
    if (!container.contains(e.target as Node)) onClose();
  };

  // A press on the trigger would move focus out and close the panel, and its click would reopen it. Inline
  // panels can sit inside their trigger, so presses inside the panel keep their default.
  const keepFocus = (e: MouseEvent) => {
    if (!container.contains(e.target as Node)) e.preventDefault();
  };

  // Leaving by Tab hands over through the restore target, which the cleanup focuses: forward to the
  // trigger's neighbour, backward to the trigger itself. Nothing after the trigger means the page ends there.
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const items = getVisibleFocusableElements(container);
    const edge = e.shiftKey ? items[0] : items[items.length - 1];
    if (!edge || document.activeElement !== edge) return;

    if (!e.shiftKey) {
      const neighbour = neighbourOfTrigger(restoreTarget, container);
      if (!neighbour) return;
      restoreTarget = neighbour;
    }
    e.preventDefault();
    onClose();
  };

  document.addEventListener('focusin', onFocusIn);
  container.addEventListener('keydown', onKeyDown);
  anchor.addEventListener('mousedown', keepFocus);

  return () => {
    cancelAnimationFrame(rafId);
    document.removeEventListener('focusin', onFocusIn);
    container.removeEventListener('keydown', onKeyDown);
    anchor.removeEventListener('mousedown', keepFocus);
    // preventScroll: a close triggered by a press elsewhere runs between that press's mousedown and
    // mouseup, so scrolling the trigger back into view would move the target out and swallow the click.
    restoreTarget?.focus({ preventScroll: true });
  };
};

const Popover = ({
  id,
  anchorEl,
  onClose,
  placement = 'bottom',
  align = 'center',
  padding = '24px',
  borderRadius = '16px',
  width,
  maxWidth,
  maxHeight,
  boxShadow,
  trigger = 'click',
  children,
  className,
  closeOnScroll: closeOnScrollProp,
  'aria-label': ariaLabel,
  title,
  footer,
  usePortal = true,
}: PopoverProps) => {
  const closeOnScroll = closeOnScrollProp ?? trigger !== 'click';
  const popoverRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const [isPositioned, setIsPositioned] = useState(false);
  const { probeRef, headerShadow, footerShadow } = useScrollShadows();

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const inline = !usePortal;
  const movesFocus = trigger !== 'hover';

  const computePosition = useCallback(() => {
    if (!anchorEl || !popoverRef.current) return;
    const coords = inline
      ? computeInlineCoords(anchorEl, popoverRef.current, placement, align)
      : computePortalCoords(anchorEl, popoverRef.current, placement, align);
    popoverRef.current.style.top = `${coords.top}px`;
    popoverRef.current.style.left = `${coords.left}px`;
  }, [anchorEl, placement, align, inline]);

  useLayoutEffect(() => {
    if (!anchorEl) {
      setIsPositioned(false);
      return;
    }
    computePosition();
    setIsPositioned(true);
  }, [anchorEl, computePosition]);

  useLayoutEffect(() => {
    if (!anchorEl) return;
    previousFocusRef.current = findRestoreTarget(anchorEl);
  }, [anchorEl]);

  useEffect(() => {
    if (!isPositioned || !popoverRef.current || !movesFocus || !anchorEl) return undefined;
    return manageFocus(popoverRef.current, anchorEl, previousFocusRef.current, () => onCloseRef.current());
  }, [isPositioned, movesFocus, anchorEl]);

  useEffect(() => {
    if (!anchorEl) return undefined;
    return bindListeners(
      anchorEl,
      popoverRef.current,
      trigger,
      () => onCloseRef.current(),
      computePosition,
      closeOnScroll
    );
  }, [anchorEl, trigger, computePosition, closeOnScroll]);

  useEffect(() => {
    if (!inline || !isPositioned || !anchorEl) return undefined;
    return elevateParentCard(anchorEl);
  }, [inline, isPositioned, anchorEl]);

  const overlayContextValue = useMemo(() => ({ headerShadow, footerShadow }), [headerShadow, footerShadow]);

  if (!anchorEl || typeof document === 'undefined') return null;

  const popoverElement = (
    <OverlayContext.Provider value={overlayContextValue}>
      <PopoverWrapper
        ref={popoverRef}
        $visible={isPositioned}
        $padding={title ? '0' : padding}
        $borderRadius={borderRadius}
        $width={width}
        $maxWidth={maxWidth ?? width ?? '320px'}
        $maxHeight={maxHeight}
        $boxShadow={boxShadow}
        $inline={inline}
        id={id}
        role="dialog"
        aria-label={ariaLabel}
        aria-hidden={!isPositioned}
        tabIndex={-1}
        className={className}
      >
        <div ref={probeRef} style={{ display: 'none' }} aria-hidden="true" />
        {title && <PopoverStickyHeader handleClose={onClose}>{title}</PopoverStickyHeader>}
        {title ? <PopoverBody $padding={padding}>{children}</PopoverBody> : children}
        {footer ?? <PopoverFooterShadow $showShadow={footerShadow} aria-hidden="true" />}
      </PopoverWrapper>
    </OverlayContext.Provider>
  );

  return inline ? popoverElement : ReactDOM.createPortal(popoverElement, document.body);
};

export default Popover;
